import React, { useState } from 'react';
import { ArrowLeft, CreditCard, Package, Truck, ShieldCheck, Lock } from 'lucide-react';

// Payment method icons (using unicode symbols for demo)
const PaymentIcon = ({ type }) => {
  const icons = {
    credit: '💳',
    debit: '💳',
    paypal: '🅿️',
    apple: '🍎',
    google: '🔍'
  };
  return <span className="text-2xl">{icons[type]}</span>;
};

export default function Checkout({ cart, cartTotal, onBack, onPlaceOrder }) {
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [formData, setFormData] = useState({
    // Shipping Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
    
    // Payment Information
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: '',
    
    // PayPal
    paypalEmail: '',
  });

  const [promoCode, setPromoCode] = useState('');
  const [discount, setDiscount] = useState(0);

  // Calculations
  const subtotal = cartTotal;
  const discountAmount = (subtotal * discount) / 100;
  const taxRate = 0.13; // 13% tax
  const taxAmount = (subtotal - discountAmount) * taxRate;
  const shippingCost = subtotal > 50 ? 0 : 9.99; // Free shipping over $50
  const total = subtotal - discountAmount + taxAmount + shippingCost;

  const handleApplyPromo = () => {
    // Simple promo code logic
    if (promoCode.toUpperCase() === 'SAVE10') {
      setDiscount(10);
      alert('Promo code applied! 10% discount');
    } else if (promoCode.toUpperCase() === 'SAVE20') {
      setDiscount(20);
      alert('Promo code applied! 20% discount');
    } else {
      alert('Invalid promo code');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.address) {
      alert('Please fill in all required shipping information');
      return;
    }

    if (paymentMethod === 'credit' || paymentMethod === 'debit') {
      if (!formData.cardNumber || !formData.cardName || !formData.expiryDate || !formData.cvv) {
        alert('Please fill in all card information');
        return;
      }
    }

    if (paymentMethod === 'paypal' && !formData.paypalEmail) {
      alert('Please enter your PayPal email');
      return;
    }

    // Process order
    onPlaceOrder({
      items: cart,
      shipping: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
      },
      payment: {
        method: paymentMethod,
      },
      pricing: {
        subtotal,
        discount: discountAmount,
        tax: taxAmount,
        shipping: shippingCost,
        total,
      }
    });
  };

  const paymentMethods = [
    { id: 'credit', name: 'Credit Card', icon: 'credit' },
    { id: 'debit', name: 'Debit Card', icon: 'debit' },
    { id: 'paypal', name: 'PayPal', icon: 'paypal' },
    { id: 'apple', name: 'Apple Pay', icon: 'apple' },
    { id: 'google', name: 'Google Pay', icon: 'google' },
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-gray-600 hover:text-red-600 mb-6"
      >
        <ArrowLeft size={20} />
        Back to Cart
      </button>

      <h1 className="text-3xl font-bold text-gray-900 mb-8">Checkout</h1>

      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 bg-red-600 text-white rounded-full font-semibold">
              1
            </div>
            <span className="ml-2 font-medium">Shipping</span>
          </div>
          <div className="w-24 h-1 bg-red-600 mx-4"></div>
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 bg-red-600 text-white rounded-full font-semibold">
              2
            </div>
            <span className="ml-2 font-medium">Payment</span>
          </div>
          <div className="w-24 h-1 bg-gray-300 mx-4"></div>
          <div className="flex items-center">
            <div className="flex items-center justify-center w-10 h-10 bg-gray-300 text-gray-600 rounded-full font-semibold">
              3
            </div>
            <span className="ml-2 text-gray-500">Review</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Forms */}
        <div className="lg:col-span-2">
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Shipping Information */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-6">
                <Truck className="text-red-600" size={24} />
                <h2 className="text-xl font-bold text-gray-900">Shipping Information</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province *
                  </label>
                  <input
                    type="text"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP/Postal Code *
                  </label>
                  <input
                    type="text"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country *
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="Australia">Australia</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard className="text-red-600" size={24} />
                <h2 className="text-xl font-bold text-gray-900">Payment Method</h2>
              </div>

              {/* Payment Method Selection */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
                {paymentMethods.map((method) => (
                  <button
                    key={method.id}
                    type="button"
                    onClick={() => setPaymentMethod(method.id)}
                    className={`p-4 border-2 rounded-lg flex flex-col items-center justify-center gap-2 transition-all ${
                      paymentMethod === method.id
                        ? 'border-red-600 bg-red-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <PaymentIcon type={method.icon} />
                    <span className="text-sm font-medium">{method.name}</span>
                  </button>
                ))}
              </div>

              {/* Credit/Debit Card Form */}
              {(paymentMethod === 'credit' || paymentMethod === 'debit') && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Number *
                    </label>
                    <input
                      type="text"
                      name="cardNumber"
                      value={formData.cardNumber}
                      onChange={handleInputChange}
                      placeholder="1234 5678 9012 3456"
                      maxLength="19"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cardholder Name *
                    </label>
                    <input
                      type="text"
                      name="cardName"
                      value={formData.cardName}
                      onChange={handleInputChange}
                      placeholder="John Doe"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date *
                      </label>
                      <input
                        type="text"
                        name="expiryDate"
                        value={formData.expiryDate}
                        onChange={handleInputChange}
                        placeholder="MM/YY"
                        maxLength="5"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVV *
                      </label>
                      <input
                        type="text"
                        name="cvv"
                        value={formData.cvv}
                        onChange={handleInputChange}
                        placeholder="123"
                        maxLength="4"
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* PayPal */}
              {paymentMethod === 'paypal' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PayPal Email *
                  </label>
                  <input
                    type="email"
                    name="paypalEmail"
                    value={formData.paypalEmail}
                    onChange={handleInputChange}
                    placeholder="your@email.com"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    You will be redirected to PayPal to complete your purchase.
                  </p>
                </div>
              )}

              {/* Apple Pay */}
              {paymentMethod === 'apple' && (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">
                    Click the button below to pay with Apple Pay
                  </p>
                  <button
                    type="button"
                    className="bg-black text-white px-8 py-3 rounded-lg font-semibold"
                  >
                    Pay with  Apple Pay
                  </button>
                </div>
              )}

              {/* Google Pay */}
              {paymentMethod === 'google' && (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">
                    Click the button below to pay with Google Pay
                  </p>
                  <button
                    type="button"
                    className="bg-white border-2 border-gray-300 text-gray-700 px-8 py-3 rounded-lg font-semibold"
                  >
                    Pay with Google Pay
                  </button>
                </div>
              )}

              {/* Security Badge */}
              <div className="mt-6 flex items-center gap-2 text-sm text-gray-500 bg-gray-50 p-4 rounded-lg">
                <Lock size={16} />
                <span>Your payment information is secure and encrypted</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-red-600 text-white py-4 rounded-lg hover:bg-red-700 font-semibold text-lg flex items-center justify-center gap-2"
            >
              <ShieldCheck size={24} />
              Place Order - ${total.toFixed(2)}
            </button>
          </form>
        </div>

        {/* Right Column - Order Summary */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-6 sticky top-24">
            <div className="flex items-center gap-3 mb-6">
              <Package className="text-red-600" size={24} />
              <h2 className="text-xl font-bold text-gray-900">Order Summary</h2>
            </div>

            {/* Cart Items */}
            <div className="space-y-4 mb-6 max-h-64 overflow-y-auto">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className="w-16 h-16 bg-gray-100 rounded flex-shrink-0">
                    {item.image && (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover rounded" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{item.name}</p>
                    <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                    <p className="text-sm font-semibold text-red-600">
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Promo Code */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Promo Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={promoCode}
                  onChange={(e) => setPromoCode(e.target.value)}
                  placeholder="Enter code"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                />
                <button
                  type="button"
                  onClick={handleApplyPromo}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium text-sm"
                >
                  Apply
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">Try: SAVE10 or SAVE20</p>
            </div>

            {/* Price Breakdown */}
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-medium">${subtotal.toFixed(2)}</span>
              </div>

              {discount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Discount ({discount}%)</span>
                  <span className="font-medium text-green-600">-${discountAmount.toFixed(2)}</span>
                </div>
              )}

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax (13%)</span>
                <span className="font-medium">${taxAmount.toFixed(2)}</span>
              </div>

              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Shipping</span>
                <span className="font-medium">
                  {shippingCost === 0 ? (
                    <span className="text-green-600">FREE</span>
                  ) : (
                    `$${shippingCost.toFixed(2)}`
                  )}
                </span>
              </div>

              <div className="border-t border-gray-200 pt-3 flex justify-between">
                <span className="text-lg font-bold">Total</span>
                <span className="text-xl font-bold text-red-600">${total.toFixed(2)}</span>
              </div>
            </div>

            {/* Free Shipping Notice */}
            {subtotal < 50 && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800">
                  Add ${(50 - subtotal).toFixed(2)} more for FREE shipping!
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}